/**
 * @file useDebounce.ts
 * @module utils/useDebounce
 * @description Provides a React hook that delays rapidly changing values to prevent unnecessary searches and API requests.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import { useEffect, useRef } from 'react';
type Timer = ReturnType<typeof setTimeout>;
type SomeFunction = (...args: any[]) => void;
export function useDebounce<Func extends SomeFunction>(
  func: Func,
  delay = 500
) {
  const timer = useRef<Timer>();
  useEffect(() => {
    return () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
    };
  }, []);
  const debouncedFunction = ((...args) => {
    const newTimer = setTimeout(() => {
      func(...args);
    }, delay);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = newTimer;
  }) as Func;

  return debouncedFunction;
}
